import { getUnprocessableErrors } from '@10xlabs/utils'
import { ApolloError, useMutation } from '@apollo/client'
import { Formik, FormikHelpers } from 'formik'
import { isEmpty } from 'lodash'
import { ReactElement, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useParams, useSearchParams } from 'react-router-dom'

import { Button } from 'components/v2/Button'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import { IVehiclesV2FormDocumentsStepValues, IVehiclesV2FormValues } from 'features/Vehicles/types'
import { CHANGE_VEHICLE_DOCUMENTS } from 'features/Vehicles/mutations-legacy'
import { usePushFileToS3 } from 'utils/file'
import { GTChangeTVehicleDocumentsDocumentInput, GTVehicleDocumentCategoryInput } from 'gql/types.generated'
import { VehicleDocumentsForm } from 'features/Vehicles/components/VehicleDocumentsForm'

const ORDERED_DOCUMENTS = [
  GTVehicleDocumentCategoryInput.Photo,
  GTVehicleDocumentCategoryInput.Bills,
  GTVehicleDocumentCategoryInput.InternalInspection,
  GTVehicleDocumentCategoryInput.ExternalInspection,
  GTVehicleDocumentCategoryInput.TuningService,
  GTVehicleDocumentCategoryInput.VerificationCertificate
]

export const NewVehiclesV2DocumentsStep = ({
  vehicle,
  onSubmit,
  onCancel
}: {
  vehicle?: IVehiclesV2FormValues
  onSubmit: () => void
  onCancel: () => void
}): ReactElement => {
  const pushFileToS3 = usePushFileToS3()
  const [searchParams] = useSearchParams()
  const params = useParams()
  const [error, setError] = useState<Error | ApolloError | null>(null)
  const vehicleId = params.vehicleId || searchParams.get('vehicleId')

  const [changeVehicleDocuments] = useMutation(CHANGE_VEHICLE_DOCUMENTS)

  const handleSubmit = async (
    values: IVehiclesV2FormDocumentsStepValues,
    { setSubmitting }: FormikHelpers<IVehiclesV2FormDocumentsStepValues>
  ): Promise<void> => {
    const documentsToUpload: GTChangeTVehicleDocumentsDocumentInput[] = []
    try {
      for (const document of values.documents) {
        if (document.file) {
          const filePath = await pushFileToS3(document.file)
          documentsToUpload.push({
            category: document.category,
            filePath
          })
        }
      }

      if (!isEmpty(documentsToUpload)) {
        await changeVehicleDocuments({
          variables: {
            id: vehicleId,
            input: {
              documents: documentsToUpload
            }
          }
        })
      }
      onSubmit()
    } catch (ex) {
      // @ts-expect-error FIXME
      setError(ex)
    }
    setSubmitting(false)
  }

  const initialValues: IVehiclesV2FormDocumentsStepValues = {
    documents: ORDERED_DOCUMENTS.map((category) => {
      const document = vehicle?.documents.find((document) => document.category === category)
      return {
        id: document?.id ?? '',
        category: category,
        file: document?.file ?? '',
        name: document?.name ?? '',
        path: document?.path ?? ''
      }
    })
  }

  return (
    <Formik onSubmit={handleSubmit} initialValues={initialValues}>
      {({ handleSubmit, dirty, isSubmitting }): ReactElement => {
        return (
          <FormLayout.Content as="form" onSubmit={handleSubmit}>
            <FormLayout.Card small>
              <VehicleDocumentsForm />
            </FormLayout.Card>
            {error && (
              <FormLayout.Actions>
                <ErrorMessage errors={getUnprocessableErrors(error as ApolloError)}>
                  <FormattedMessage
                    id="vehicles.form.documents.error"
                    defaultMessage="There was an error while uploading the documents"
                  />
                </ErrorMessage>
              </FormLayout.Actions>
            )}
            <FormLayout.Actions>
              <Button type="button" kind="cancel" onClick={onCancel} disabled={isSubmitting}>
                <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
              </Button>
              <Button type="submit" kind="primary" disabled={!dirty} loading={isSubmitting}>
                <FormattedMessage id="vehicles.form.documents.save" defaultMessage="Save" />
              </Button>
            </FormLayout.Actions>
          </FormLayout.Content>
        )
      }}
    </Formik>
  )
}
